/**
 * Chapter005.js — Hazard Categories (OHSE 005)
 * Migrated from SWMS Platform Chapter005.tsx → JavaScript
 *
 * Props:
 *   data      {object}   hazard_categories from swmsData
 *   onChange  {fn}       (newData) => void — caller saves to PATCH
 *   companyName {string} derived from companyInfo.name
 */

import { useState } from 'react';
import { Plus, X } from 'lucide-react';

let _customIdCounter = 1;

// ── Checklist definitions ────────────────────────────────────────────────────

const OHS_ITEMS = [
  { key: 'access_egress',              label: 'Access & egress' },
  { key: 'confined_enclosed_spaces',   label: 'Confined/enclosed spaces' },
  { key: 'coring_chasing',             label: 'Coring/chasing' },
  { key: 'dangerous_goods',            label: 'Dangerous Goods (Oxy/other)' },
  { key: 'demolition_dismantling',     label: 'Demolition/dismantling' },
  { key: 'electricity',                label: 'Electricity (power tools/other)' },
  { key: 'explosive_pneumatic_tools',  label: 'Explosive/pneumatic power tools' },
  { key: 'fatigue',                    label: 'Fatigue (shift work/hours of work)' },
  { key: 'formwork',                   label: 'Formwork erection/dismantling' },
  { key: 'fire_explosion',             label: 'Fire/explosion' },
  { key: 'fumes_gas',                  label: 'Fumes/gas' },
  { key: 'hazardous_substances',       label: 'Hazardous substances' },
  { key: 'flying_falling_objects',     label: 'Flying/falling objects/debris' },
  { key: 'height_falls',               label: 'Height & falls' },
  { key: 'hazardous_material',         label: 'Hazardous material' },
  { key: 'hot_cold_environment',       label: 'Hot/cold working environment' },
  { key: 'hot_work',                   label: 'Hot work (cutting/welding/grinding)' },
  { key: 'lasers',                     label: 'Lasers' },
  { key: 'lighting',                   label: 'Lighting' },
  { key: 'manual_handling',            label: 'Manual handling (lifting or twisting)' },
  { key: 'machine_equipment_guarding', label: 'Machine/equipment guarding' },
  { key: 'moving_plant_traffic',       label: 'Moving plant/traffic' },
  { key: 'materials_handling',         label: 'Materials handling (crane/forklift/other)' },
  { key: 'plant_equipment_operation',  label: 'Plant & equipment operation' },
  { key: 'noise_hearing',              label: 'Noise (hearing)' },
  { key: 'structural_alterations',     label: 'Structural alterations/support' },
  { key: 'public_pedestrians',         label: 'Public (pedestrians/other)' },
  { key: 'services_underground_overhead', label: 'Services (underground/overhead)' },
  { key: 'subsidence',                 label: 'Subsidence' },
  { key: 'ultraviolet_light',          label: 'Ultra Violet Light (sunlight)' },
  { key: 'trenching_excavation',       label: 'Trenching/excavation' },
  { key: 'work_near_water',            label: 'Work near/over water' },
  { key: 'young_workers',              label: 'Young workers/unskilled labour' },
  { key: 'biological_bacteria',        label: 'Biological/bacteria' },
];

const ENV_ITEMS = [
  { key: 'air_quality',                    label: 'Air quality (dust/emissions)' },
  { key: 'bulk_excavation',               label: 'Bulk excavation/spoil' },
  { key: 'concrete_paint_wastes',         label: 'Concrete or paint wastes' },
  { key: 'contaminated_soil_water',       label: 'Contaminated soil/water' },
  { key: 'dewatering',                    label: 'Dewatering/pump out' },
  { key: 'habitats',                      label: 'Habitats (protected flora/fauna)' },
  { key: 'heritage_archaeology',          label: 'Heritage & Archaeology' },
  { key: 'noise_vibration',              label: 'Noise or vibration' },
  { key: 'noisy_work_neighbourhood',      label: 'Noisy work (neighbourhood)' },
  { key: 'spills_response',              label: 'Spills & response' },
  { key: 'slurry_discharges',            label: 'Slurry or other discharges' },
  { key: 'traffic_parking',              label: 'Traffic & parking' },
  { key: 'waste_hazardous',              label: 'Waste hazardous (paint sludge, synthetic min fibre, asbestos/other)' },
  { key: 'dangerous_goods_substances',   label: 'Dangerous Goods/Hazardous Substances (use/storage/spills)' },
  { key: 'stormwater_sediment',          label: 'Stormwater/sediment control' },
];

// ── Sub-components ───────────────────────────────────────────────────────────

function CheckRow({ item, checked, onToggle }) {
  return (
    <div className="flex items-center gap-3 py-2 px-3 border-b border-[#e0dbd0] last:border-b-0 hover:bg-[#f8f7f4]/50 transition-colors">
      <label className="flex items-center gap-3 flex-1 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!!checked}
          onChange={() => onToggle()}
          className="w-4 h-4 rounded border-[#c9a84c] accent-[#c9a84c] cursor-pointer"
        />
        <span className="text-sm text-[#1a1a2e]">{item.label}</span>
      </label>
    </div>
  );
}

function CustomCheckRow({ item, onToggle, onLabelChange, onDelete }) {
  return (
    <div className="flex items-center gap-2 py-2 px-3 border-b border-[#e0dbd0] last:border-b-0 hover:bg-[#f8f7f4]/50 transition-colors group">
      <label className="flex items-center gap-3 flex-1 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!!item.checked}
          onChange={() => onToggle()}
          className="w-4 h-4 rounded border-[#c9a84c] accent-[#c9a84c] cursor-pointer"
        />
        <input
          type="text"
          value={item.label}
          onChange={e => onLabelChange(e.target.value)}
          placeholder="Enter hazard name..."
          className="text-sm text-[#1a1a2e] bg-transparent border-b border-transparent hover:border-[#c9a84c]/30 focus:border-[#c9a84c] focus:outline-none flex-1 px-0.5 py-0.5 transition-all"
        />
      </label>
      <button
        onClick={onDelete}
        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity p-0.5"
        title="Remove this item"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function CheckGroup({ title, items, customItems, data, onChange, customKey }) {
  const toggle = (key) => onChange({ ...data, [key]: !data[key] });

  const addCustom = () => {
    const newItem = {
      id: `custom_${customKey}_${_customIdCounter++}`,
      checked: true,
      label: '',
    };
    onChange({ ...data, [customKey]: [...(customItems || []), newItem] });
  };

  const updateCustom = (id, patch) => {
    onChange({
      ...data,
      [customKey]: (customItems || []).map(c => c.id === id ? { ...c, ...patch } : c),
    });
  };

  const deleteCustom = (id) => {
    onChange({
      ...data,
      [customKey]: (customItems || []).filter(c => c.id !== id),
    });
  };

  return (
    <div className="border border-[#e0dbd0] rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 bg-[#1a1a2e] text-white text-sm font-semibold tracking-wide">
        {title}
      </div>
      {items.map(item => (
        <CheckRow
          key={item.key}
          item={item}
          checked={data[item.key]}
          onToggle={() => toggle(item.key)}
        />
      ))}
      {(customItems || []).map(item => (
        <CustomCheckRow
          key={item.id}
          item={item}
          onToggle={() => updateCustom(item.id, { checked: !item.checked })}
          onLabelChange={label => updateCustom(item.id, { label })}
          onDelete={() => deleteCustom(item.id)}
        />
      ))}
      <div className="px-3 py-2 border-t border-[#e0dbd0]">
        <button
          onClick={addCustom}
          className="inline-flex items-center gap-1.5 text-xs text-[#c9a84c] hover:text-[#c9a84c]/80 font-medium transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Item
        </button>
      </div>
    </div>
  );
}

// ── Main export ──────────────────────────────────────────────────────────────

export default function Chapter005({ data = {}, onChange, companyName }) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 005</p>
          <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">Hazard Categories</h2>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-[#1a1a2e] leading-relaxed">
        The following is a list of the hazards{' '}
        <span className="font-semibold text-[#c9a84c]">{companyName || '[Company Name]'}</span>{' '}
        has identified arising from the contracted/agreed work activities. These hazards are addressed within the Safe Work Method Statement(s).
      </p>

      {/* Checklist grids */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CheckGroup
          title="Occupational Health and Safety"
          items={OHS_ITEMS}
          customItems={data.custom_ohs}
          data={data}
          onChange={onChange}
          customKey="custom_ohs"
        />
        <CheckGroup
          title="Environment"
          items={ENV_ITEMS}
          customItems={data.custom_env}
          data={data}
          onChange={onChange}
          customKey="custom_env"
        />
      </div>

      <p className="text-xs text-gray-400">
        Checked items are saved automatically.
      </p>
    </div>
  );
}
